import Vue from 'vue';
import api from '@/apis/Api';
import { DashboardApi } from '@/apis/dashboard.api';
import { ExamineOutputApi } from '@/apis/examine.output.api';
import { ImagePolygonMapManager } from '@/components/map/ImagePolygonMapManager';
const state = {
  // 프로젝트, 사이트, 보고서 데이터
  projects: [],
  sites: [],
  reports: [],

  // 선택된 항목들
  selectedProject: null,
  selectedReport: null,

  // UI 상태
  showExplorer: true, // 네비게이션 표시 여부 (CoreLeftDrawer와 동일)
  loading: true, // 초기 로딩 상태를 true로 설정

  // 이미지 데이터
  thermalImages: [],
  selectedImages: [], // 선택된 이미지들

  // 검색
  keyword: ''
};

const getters = {
  // 프로젝트 목록
  getProjects: (state) => state.projects,

  // 사이트 목록
  getSites: (state) => state.sites,

  // 보고서 목록
  getReports: (state) => state.reports,

  // 선택된 프로젝트
  getSelectedProject: (state) => state.selectedProject,

  // 선택된 사이트 (siteData store와 동기화)
  getSelectedSite: (state, getters, rootState, rootGetters) => {
    return rootGetters['siteData/getSelectedSite'];
  },

  // 선택된 보고서
  getSelectedReport: (state) => state.selectedReport,

  // 네비게이션 표시 여부
  getShowExplorer: (state) => state.showExplorer,

  // 로딩 상태
  getLoading: (state) => state.loading,

  // 열화상 이미지 목록
  getThermalImages: (state) => state.thermalImages,

  // 선택된 이미지 목록
  getSelectedImages: (state) => state.selectedImages,

  // 검색 키워드
  getKeyword: (state) => state.keyword
};

const mutations = {
  // 프로젝트 목록 설정
  SET_PROJECTS(state, projects) {
    Vue.set(state, 'projects', projects);
  },

  // 사이트 목록 설정
  SET_SITES(state, sites) {
    Vue.set(state, 'sites', sites);
  },

  // 보고서 목록 설정
  SET_REPORTS(state, reports) {
    Vue.set(state, 'reports', reports);
  },

  // 선택된 프로젝트 설정
  SET_SELECTED_PROJECT(state, project) {
    Vue.set(state, 'selectedProject', project);
  },

  // 선택된 보고서 설정
  SET_SELECTED_REPORT(state, report) {
    Vue.set(state, 'selectedReport', report);
  },

  // 네비게이션 표시 여부 설정
  SET_SHOW_EXPLORER(state, showExplorer) {
    Vue.set(state, 'showExplorer', showExplorer);
  },

  // 로딩 상태 설정
  SET_LOADING(state, loading) {
    Vue.set(state, 'loading', loading);
  },

  // 열화상 이미지 목록 설정
  SET_THERMAL_IMAGES(state, thermalImages) {
    Vue.set(state, 'thermalImages', thermalImages);
  },

  // 선택된 이미지 목록 설정
  SET_SELECTED_IMAGES(state, selectedImages) {
    Vue.set(state, 'selectedImages', selectedImages);
  },
  // 검색 키워드 설정
  SET_KEYWORD(state, keyword) {
    Vue.set(state, 'keyword', keyword);
  },

  // 이미지 좌표 업데이트
  UPDATE_IMAGE_COORDINATES(state, { imageId, coordinates, centerCoordinate }) {
    console.log('[IMAGE ALIGNMENT STORE] 이미지 좌표 업데이트', {
      imageId,
      coordinates,
      centerCoordinate
    });

    // thermalImages 업데이트
    const thermalImageIndex = state.thermalImages.findIndex(img => img.id === imageId);
    if (thermalImageIndex !== -1) {
      Vue.set(state.thermalImages[thermalImageIndex], 'imgPosCoords', {
        coordinates: [coordinates]
      });
      console.log('[IMAGE ALIGNMENT STORE] thermalImages 업데이트 완료', { imageId });
    } else {
      console.warn('[IMAGE ALIGNMENT STORE] thermalImages에서 이미지를 찾을 수 없음', { imageId });
    }

    // selectedImages 업데이트 (중복 제거)
    const selectedImageIndex = state.selectedImages.findIndex(img => img.id === imageId);
    if (selectedImageIndex !== -1) {
      Vue.set(state.selectedImages[selectedImageIndex], 'imgPosCoords', {
        coordinates: [coordinates]
      });
      console.log('[IMAGE ALIGNMENT STORE] selectedImages 업데이트 완료', { imageId });
    } else {
      console.warn('[IMAGE ALIGNMENT STORE] selectedImages에서 이미지를 찾을 수 없음', { imageId });
    }
  },

  // 상태 초기화
  RESET_STATE(state) {
    Vue.set(state, 'projects', []);
    Vue.set(state, 'sites', []);
    Vue.set(state, 'reports', []);
    Vue.set(state, 'selectedImages', []);
    Vue.set(state, 'selectedProject', null);

    Vue.set(state, 'selectedReport', null);
    Vue.set(state, 'showExplorer', true);
    Vue.set(state, 'loading', false);
    Vue.set(state, 'thermalImages', []);
    Vue.set(state, 'keyword', '');
  }
};

const actions = {
  // 열화상 이미지 조정 페이지 초기화
  /**
   *
   * @param {*} param0
   * @returns thermalImages
   */
  async initializeImageAlignment({ dispatch, state }) {
    try {
      await dispatch('fetchProjects');
      if(state.selectedReport){
        return await dispatch('fetchAndStoreThermalImages', state.selectedReport.id);
      }
    } catch (error) {
      console.error('[IMAGE ALIGNMENT STORE] 초기화 실패:', error);
      throw error;
    }
  },

  // 프로젝트 목록 가져오기
  async fetchProjects({ commit }) {
    try {
      const response = await api.read('/dashboard/navigation');
      const projects = response.data.projects || [];
      console.log('[IMAGE ALIGNMENT STORE] 프로젝트 목록:', projects);
      commit('SET_PROJECTS', projects);
    } catch (error) {
      console.error('[IMAGE ALIGNMENT STORE] 프로젝트 목록 가져오기 실패:', error);
      commit('SET_PROJECTS', []);
      throw error; // 상위에서 처리할 수 있도록 에러를 다시 던짐
    }
  },

  // 프로젝트 검색
  async searchProjects({ commit, state }, keyword) {
    try {
      commit('SET_KEYWORD', keyword);

      if (!keyword || keyword.trim() === '') {
        // 키워드가 없으면 전체 프로젝트 반환
        const response = await api.read('/dashboard/navigation');
        return response.data.projects || [];
      }

      // 키워드로 검색
      const response = await api.read('/dashboard/navigation');
      const allProjects = response.data.projects || [];

      // 프로젝트명과 사이트명에서 키워드 검색
      const filteredProjects = allProjects.filter(project => {
        // 프로젝트명에서 검색
        if (project.name && project.name.toLowerCase().includes(keyword.toLowerCase())) {
          return true;
        }

        // 사이트명에서 검색
        if (project.children && project.children.length > 0) {
          const hasMatchingSite = project.children.some(site =>
            site.name && site.name.toLowerCase().includes(keyword.toLowerCase())
          );
          if (hasMatchingSite) {
            return true;
          }
        }

        return false;
      });

      return filteredProjects;
    } catch (error) {
      console.error('프로젝트 검색 실패:', error);
      return [];
    }
  },

  // 사이트 선택 (siteData store로 위임)
  async selectSite({ commit, dispatch }, site) {
    try {
      // siteData store에서 사이트 선택 처리
      await dispatch('siteData/selectSite', site, { root: true });

      // ImageAlignment 관련 상태 업데이트
      commit('SET_SELECTED_REPORT', null);
      commit('SET_SHOW_EXPLORER', true);
      commit('SET_THERMAL_IMAGES', []);

    } catch (error) {
      console.error('사이트 선택 실패:', error);
    }
  },

  // 보고서 선택
  async selectReport({ commit, dispatch }, report) {
    try {
      console.log('[IMAGE ALIGNMENT STORE] 보고서 선택 시작:', {
        reportId: report.id,
        reportName: report.name
      });

      commit('SET_LOADING', true);
      commit('SET_SELECTED_REPORT', report);

      // AI Effect API 호출 및 홀수/짝수 이미지 분리
      await dispatch('fetchAndStoreThermalImages', report.id);

    } catch (error) {
      console.error('보고서 선택 실패:', error);
      commit('SET_THERMAL_IMAGES', []);
      commit('SET_LOADING', false);
    }
  },

  async fetchAndStoreThermalImages({ commit, state}, reportId) {
    try {
      if(state.loading == false){
        commit('SET_LOADING', true);
      }

      // 1. AI Effect API 호출
      const response = await ExamineOutputApi.getThermalImages(reportId, false);
      const thermalImages = response.data.content || [];

      // 2. 전체 이미지를 store에 저장
      commit('SET_THERMAL_IMAGES', thermalImages);

      commit('SET_LOADING', false);
      return thermalImages;
    } catch (error) {
      console.error('[IMAGE ALIGNMENT STORE] AI Effect API 호출 및 홀수/짝수 이미지 분리 실패:', error);
      commit('SET_THERMAL_IMAGES', []);
      commit('SET_LOADING', false);
      throw error;
    }
  },
  // 로딩 상태 설정
  setLoading({ commit }, loading) {
    commit('SET_LOADING', loading);
  },

  changeSelectedImages({ commit }, selectedImages) {
    commit('SET_SELECTED_IMAGES', selectedImages);
  },

  // 선택된 이미지 목록 설정
  setSelectedImages({ commit }, selectedImages) {
    commit('SET_SELECTED_IMAGES', selectedImages);
  },

  // 네비게이션 표시 여부 설정
  setShowExplorer({ commit }, showExplorer) {
    commit('SET_SHOW_EXPLORER', showExplorer);
  },

  // 이미지 리스트 닫기 (뒤로가기)
  closeImageList({ commit }) {
    commit('SET_SHOW_EXPLORER', true);
    commit('SET_SELECTED_REPORT', null);
    commit('SET_THERMAL_IMAGES', []);
  },

  // 네비게이션으로 돌아가기
  returnToNavigation({ commit }) {
    commit('SET_SHOW_EXPLORER', true);
    commit('SET_SELECTED_REPORT', null);
    commit('SET_SELECTED_SITE', null);
    commit('SET_THERMAL_IMAGES', []);
  }


};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions
};
